/**
 * LINE ニュース AI 生成ダイアログ。
 * 仕様書: docs/01_spec/spec_line_bot.md A-10.5（Phase 22e）
 *
 * 2 モード:
 *   - free: 自由記述プロンプト（テンプレート不要）
 *   - template: テンプレート + フィールド入力 + プレセット流用
 */
import { Save, Sparkles } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input, Label, Textarea } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/components/ui/toast';
import { useGenerateLineNews } from '@/hooks/useLineNews';
import { usePromptPresets, usePromptTemplates } from '@/hooks/usePromptData';

import { PresetFormDialog } from '@/pages/prompts/presets/PresetFormDialog';

import type { LineNews } from '@goenhub/shared';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  hasExistingSections: boolean;
  onApply: (next: {
    title: string;
    sections: LineNews['sections'];
    modelUsed: string;
  }) => void;
}

type Mode = 'free' | 'template';

export function NewsAiGenerateDialog({
  open,
  onOpenChange,
  hasExistingSections,
  onApply,
}: Props) {
  const generateMutation = useGenerateLineNews();
  const { data: templates } = usePromptTemplates({ scope: 'line_news' });
  const [mode, setMode] = useState<Mode>('free');
  const [description, setDescription] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [formValues, setFormValues] = useState<Record<string, string>>({});
  const [presetId, setPresetId] = useState('');
  const [presetDialogOpen, setPresetDialogOpen] = useState(false);

  const { data: presets } = usePromptPresets({ templateId: templateId || undefined });

  // プレセット保存ボタン表示判定（最低 1 つの入力値があるとき）
  const hasFormValues = useMemo(
    () => Object.values(formValues).some((v) => v.trim().length > 0),
    [formValues],
  );

  const selectedTemplate = useMemo(
    () => templates?.find((t) => t.id === templateId),
    [templates, templateId],
  );

  useEffect(() => {
    if (!open) return;
    setMode(templates && templates.length > 0 ? 'template' : 'free');
    setDescription('');
    setTemplateId(templates?.[0]?.id ?? '');
    setFormValues({});
    setPresetId('');
  }, [open, templates]);

  // テンプレート切替時はフォーム値クリア
  useEffect(() => {
    setFormValues({});
    setPresetId('');
  }, [templateId]);

  // プレセット選択時はフォーム値を流し込む
  useEffect(() => {
    if (!presetId || !presets) return;
    const p = presets.find((x) => x.id === presetId);
    if (p) setFormValues({ ...p.values });
  }, [presetId, presets]);

  const handleGenerate = async () => {
    if (
      hasExistingSections &&
      !window.confirm(
        '既存のセクションは AI 生成結果で上書きされます。よろしいですか？',
      )
    ) {
      return;
    }

    try {
      let res;
      if (mode === 'free') {
        const desc = description.trim();
        if (!desc) {
          toast.error('内容説明を入力してください');
          return;
        }
        res = await generateMutation.mutateAsync({
          templateId: '__free',
          freePrompt: desc,
        });
      } else {
        if (!templateId) {
          toast.error('テンプレートを選択してください');
          return;
        }
        res = await generateMutation.mutateAsync({
          templateId,
          formValues,
        });
      }
      onApply({
        title: res.generated.title,
        sections: res.generated.sections,
        modelUsed: res.modelUsed,
      });
      toast.success(
        `AI で ${res.generated.sections.length} セクション生成しました`,
      );
      onOpenChange(false);
    } catch (err) {
      toast.error(
        `生成失敗: ${err instanceof Error ? err.message : String(err)}`,
      );
    }
  };

  const hasTemplates = (templates?.length ?? 0) > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            <Sparkles className="size-4 inline mr-1.5 text-[var(--accent-purple)]" />
            AI でニュース構成を生成
          </DialogTitle>
          <DialogDescription>
            テンプレートまたは自由記述で Claude にニュースを生成させます。
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* モード切替 */}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setMode('template')}
              disabled={!hasTemplates}
              className={
                'flex-1 px-3 py-2 rounded-sm text-xs border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ' +
                (mode === 'template'
                  ? 'border-[var(--accent)] bg-[var(--accent-subtle)] text-[var(--accent)]'
                  : 'border-[var(--border)] hover:border-[var(--text-muted)]')
              }
            >
              テンプレート利用
            </button>
            <button
              type="button"
              onClick={() => setMode('free')}
              className={
                'flex-1 px-3 py-2 rounded-sm text-xs border transition-colors ' +
                (mode === 'free'
                  ? 'border-[var(--accent)] bg-[var(--accent-subtle)] text-[var(--accent)]'
                  : 'border-[var(--border)] hover:border-[var(--text-muted)]')
              }
            >
              自由記述
            </button>
          </div>

          {mode === 'free' && (
            <div>
              <Label className="text-xs">ニュースの内容（必須）</Label>
              <Textarea
                rows={6}
                placeholder={
                  '例: 4/21（土）に体験会を開催。会場: 大阪・堂島。\n' +
                  '残席わずか、無料申込はフォームから。'
                }
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                autoFocus
              />
            </div>
          )}

          {mode === 'template' && hasTemplates && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">テンプレート</Label>
                  <Select value={templateId} onValueChange={setTemplateId}>
                    <SelectTrigger>
                      <SelectValue placeholder="テンプレート選択" />
                    </SelectTrigger>
                    <SelectContent>
                      {templates?.map((t) => (
                        <SelectItem key={t.id} value={t.id}>
                          {t.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">プレセットから読込（任意）</Label>
                  <Select
                    value={presetId || '__none__'}
                    onValueChange={(v) => setPresetId(v === '__none__' ? '' : v)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="プレセット選択" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">（未選択）</SelectItem>
                      {presets?.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {selectedTemplate && selectedTemplate.fields.length > 0 ? (
                <div className="space-y-2 pt-2 border-t border-[var(--border)]">
                  {selectedTemplate.fields.map((f) => (
                    <div key={f.id}>
                      <Label className="text-xs">{f.label}</Label>
                      {f.type === 'textarea' ? (
                        <Textarea
                          value={formValues[f.id] ?? ''}
                          onChange={(e) =>
                            setFormValues((s) => ({ ...s, [f.id]: e.target.value }))
                          }
                          placeholder={f.placeholder}
                          rows={2}
                        />
                      ) : f.type === 'select' ? (
                        <Select
                          value={formValues[f.id] ?? ''}
                          onValueChange={(v) =>
                            setFormValues((s) => ({ ...s, [f.id]: v }))
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder={f.placeholder || '選択...'} />
                          </SelectTrigger>
                          <SelectContent>
                            {f.options.map((opt) => (
                              <SelectItem key={opt} value={opt}>
                                {opt}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input
                          type="text"
                          value={formValues[f.id] ?? ''}
                          onChange={(e) =>
                            setFormValues((s) => ({ ...s, [f.id]: e.target.value }))
                          }
                          placeholder={f.placeholder}
                        />
                      )}
                    </div>
                  ))}
                  {hasFormValues && (
                    <div className="flex justify-end pt-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setPresetDialogOpen(true)}
                        title="現在の入力値をプレセットとして保存"
                      >
                        <Save className="size-3.5 mr-1" />
                        プレセットとして保存
                      </Button>
                    </div>
                  )}
                </div>
              ) : selectedTemplate ? (
                <p className="text-xs text-[var(--text-muted)] italic">
                  選択したテンプレートにフィールドがありません
                </p>
              ) : null}
            </div>
          )}

          {hasExistingSections && (
            <div className="text-[11px] text-[var(--accent-amber)] bg-[var(--accent-amber-subtle)] rounded px-2 py-1.5">
              ⚠ 既存のセクションは AI 生成結果で上書きされます
            </div>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            キャンセル
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={handleGenerate}
            disabled={
              generateMutation.isPending ||
              (mode === 'free' && !description.trim()) ||
              (mode === 'template' && !templateId)
            }
          >
            {generateMutation.isPending ? (
              <Spinner className="size-3 mr-1" />
            ) : (
              <Sparkles className="size-4 mr-1" />
            )}
            生成
          </Button>
        </DialogFooter>
      </DialogContent>

      <PresetFormDialog
        open={presetDialogOpen}
        onOpenChange={setPresetDialogOpen}
        seed={
          mode === 'template' && templateId
            ? { templateId, values: formValues }
            : undefined
        }
        scope="line_news"
      />
    </Dialog>
  );
}
